import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { Joyride, STATUS, type Step, type State } from 'react-joyride';
import { useAuth } from '@/context/AuthContext';

// ── Tour step definitions ──────────────────────────────

const WORK_ORDER_STEPS: Step[] = [
  {
    target: '[data-tour="nav-operations"]',
    content: 'Welcome! The Operations menu contains your Dashboard, Work Orders, and Assets — the core of daily operations.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '[data-tour="nav-processing-group"]',
    content: 'Processing covers the full lifecycle: triage, data wipe, repair, and QA. Track every asset through each stage.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="nav-logistics"]',
    content: 'Logistics handles shipments and compliance — manage outbound loads and regulatory requirements.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="scan-button"]',
    content: 'Use the Scan button to quickly look up any asset or work order by barcode using your device camera.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="location-selector"]',
    content: 'Switch between your organization\'s sites to see location-specific data.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="nav-admin"]',
    content: 'Admin gives you access to compliance docs, reports, user management, and system settings.',
    placement: 'bottom',
  },
];

const CRM_STEPS: Step[] = [
  {
    target: '[data-tour="nav-crm-dashboard"]',
    content: 'Welcome to the CRM! Start here on the Dashboard for a bird\'s-eye view of your pipeline, upcoming appointments, and recent activity.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '[data-tour="nav-crm-customers"]',
    content: 'Customers holds your company accounts — add, edit, and manage the organisations you do business with.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="nav-crm-contacts"]',
    content: 'Contacts stores the people at each customer. Every contact is linked to a customer account.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="nav-crm-deals"]',
    content: 'Deals is your sales pipeline — track opportunities from lead through to won or lost, with values and stages.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="nav-crm-appointments"]',
    content: 'Appointments lets you schedule and track meetings, calls, and site visits tied to your contacts and deals.',
    placement: 'bottom',
  },
];

// ── Storage key ────────────────────────────────────────

const TOUR_COMPLETED_KEY = 'erp_tour_completed';
const TOUR_DISMISSED_KEY = 'erp_tour_dismissed';

// ── Context ────────────────────────────────────────────

export type TourType = 'work-orders' | 'crm';

interface TourContextType {
  startTour: (type: TourType) => void;
  showChoiceDialog: boolean;
  setShowChoiceDialog: (v: boolean) => void;
  dismissChoice: () => void;
  hasCompletedAnyTour: boolean;
}

const TourContext = createContext<TourContextType>({
  startTour: () => {},
  showChoiceDialog: false,
  setShowChoiceDialog: () => {},
  dismissChoice: () => {},
  hasCompletedAnyTour: false,
});

export const useTour = () => useContext(TourContext);

// ── Provider ───────────────────────────────────────────

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [runTour, setRunTour] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [hasCompletedAnyTour, setHasCompletedAnyTour] = useState(false);

  // Check if user has seen the tour choice before
  useEffect(() => {
    if (isAuthenticated && user) {
      const dismissed = localStorage.getItem(TOUR_DISMISSED_KEY);
      const completed = localStorage.getItem(TOUR_COMPLETED_KEY);
      if (completed) {
        setHasCompletedAnyTour(true);
      }
      if (!dismissed && !completed) {
        const timer = setTimeout(() => setShowChoiceDialog(true), 1200);
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, user]);

  const startTour = useCallback((type: TourType) => {
    // Navigate to the starting page for the tour
    if (type === 'crm') {
      window.history.pushState({}, '', '/crm');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }

    setSteps(type === 'crm' ? CRM_STEPS : WORK_ORDER_STEPS);
    setShowChoiceDialog(false);
    setTimeout(() => setRunTour(true), 800);
  }, []);

  const dismissChoice = useCallback(() => {
    localStorage.setItem(TOUR_DISMISSED_KEY, 'true');
    setShowChoiceDialog(false);
  }, []);

  const handleCallback = useCallback((state: State) => {
    const { status } = state;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      if (status === STATUS.FINISHED) {
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
        setHasCompletedAnyTour(true);
      }
    }
  }, []);

  return (
    <TourContext.Provider
      value={{ startTour, showChoiceDialog, setShowChoiceDialog, dismissChoice, hasCompletedAnyTour }}
    >
      {children}
      {runTour && (
        <Joyride
          steps={steps}
          run={runTour}
          continuous
          scrollToFirstStep
          onEvent={handleCallback as any}
          options={{
            showProgress: true,
            primaryColor: 'hsl(215, 80%, 48%)',
            overlayColor: 'rgba(0, 0, 0, 0.45)',
            buttons: ['back', 'skip', 'primary'],
          }}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Finish',
            next: 'Next',
            skip: 'Skip tour',
          }}
        />
      )}
    </TourContext.Provider>
  );
}
